import ReactRootProviders from '@client/src/global/context/ReactRootProviders';
import { UserSessionProvider } from '@client/src/global/context/UserSessionProvider';
import React, { lazy, Suspense } from 'react';
import { react2angular } from 'react2angular';
import { CategoryHSCodeSelectionValue } from '@/components/CategoryHSCodeSelection/types';
import {
  CategoryHsCodeSelection,
  CategorySelection,
  HsCodeSelection,
} from '@client/src/global/components/category-hs-code/types';
import {
  isCategoryHSCategoryOption,
  isCategoryHSSuggestedOption,
} from '@app/components/CategoryHSCodeSelection/hooks/useCategoryHSCode';
import { twMerge } from 'tailwind-merge';

const CategoryHSCodeSelection = lazy(() => import('@/components/CategoryHSCodeSelection'));

export interface CategoryHsCodeInputProps {
  value: CategoryHsCodeSelection;
  onCategorySelect: (categoryId: number) => void;
  onHsCodeSelect: (hsCodeNumber: string, hsCodeDescription: string) => void;
  className?: string | undefined;
  error?: boolean;
  hsError?: boolean;
  is10DigitCodeEnabled?: boolean;
}

function WrappedComponent({
  hsError,
  error,
  className,
  onCategorySelect,
  onHsCodeSelect,
  value,
  is10DigitCodeEnabled,
}: CategoryHsCodeInputProps) {
  const onChange = (value: CategoryHSCodeSelectionValue) => {
    if (isCategoryHSCategoryOption(value)) {
      onCategorySelect(value.id);
      return;
    }
    if (isCategoryHSSuggestedOption(value)) {
      onHsCodeSelect(value.code, value.description || '');
    }
  };

  const data = (() => {
    if (!value) return undefined;
    if ((value as CategorySelection)?.categoryId) {
      const id = Number((value as CategorySelection)?.categoryId);
      return id ? { id } : undefined;
    }
    if ((value as HsCodeSelection)?.hsCodeNumber) {
      return {
        code: (value as HsCodeSelection)?.hsCodeNumber,
        description: (value as HsCodeSelection)?.hsCodeDescription,
      };
    }
    return undefined;
  })();

  return (
    <ReactRootProviders>
      <UserSessionProvider>
        <Suspense fallback={null}>
          <CategoryHSCodeSelection
            is10DigitCodeEnabled={is10DigitCodeEnabled}
            status={hsError || error ? 'error' : 'default'}
            className={twMerge('py-[9px] mt-[-1px]', className)}
            onChange={onChange}
            value={data}
          />
        </Suspense>
      </UserSessionProvider>
    </ReactRootProviders>
  );
}

export const AngularCategoryHSCodeSelection = react2angular(WrappedComponent, [
  'error',
  'hsError',
  'value',
  'onCategorySelect',
  'onHsCodeSelect',
  'className',
  'is10DigitCodeEnabled',
]);
